/* src/contexts/profile/profileActionsSessions.js */
import { useCallback } from 'react';

/**
 * Hook creator for Fetch Paginated Sessions action.
 */
export const useCreateFetchPaginatedSessionsAction = (
    authUserRef,
    fetchUserSessionsFn // Cloud function ref
) => {
    return useCallback(async (profileId, options = {}) => {
        const currentUserId = authUserRef.current?.uid;
        console.log(`[fetchPaginatedSessions Action] Called. Profile: ${profileId}, User: ${currentUserId}`);
        if (!currentUserId || !profileId) {
            console.warn("[fetchPaginatedSessions Action] Auth/profile ID missing.");
            return { success: false, error: "Authentication or profile ID missing.", sessions: [], totalFilteredSessions: 0, lastVisibleDocId: null };
        }
        if (!fetchUserSessionsFn) {
            console.error("[fetchPaginatedSessions Action] Cloud function reference not available.");
            return { success: false, error: "Session fetching service unavailable.", sessions: [], totalFilteredSessions: 0, lastVisibleDocId: null };
        }

        const { pageSize = 25, startDate = null, endDate = null, lastVisibleDocId = null } = options;
        const payload = { profileId, pageSize, startDate, endDate, lastVisibleDocId };

        try {
            console.log("[fetchPaginatedSessions Action] Calling backend with payload:", payload);
            const result = await fetchUserSessionsFn(payload);

            if (result?.data?.success) {
                console.log(`[fetchPaginatedSessions Action] Success. Fetched: ${result.data.sessions?.length}, Total: ${result.data.totalFilteredSessions}, LastVisible: ${result.data.lastVisibleDocId}`);
                return {
                    success: true,
                    sessions: result.data.sessions || [],
                    totalFilteredSessions: result.data.totalFilteredSessions ?? 0,
                    lastVisibleDocId: result.data.lastVisibleDocId || null,
                };
            } else {
                const errorMessage = result?.data?.message || "Failed to fetch sessions.";
                console.error(`[fetchPaginatedSessions Action] Backend function failed: ${errorMessage}`, result?.data);
                return { success: false, error: errorMessage, sessions: [], totalFilteredSessions: 0, lastVisibleDocId: null };
            }
        } catch (err) {
            console.error("[fetchPaginatedSessions Action] Error calling cloud function:", err);
            const message = err.message || "An unexpected error occurred fetching sessions.";
            return { success: false, error: message, sessions: [], totalFilteredSessions: 0, lastVisibleDocId: null };
        }
    }, [authUserRef, fetchUserSessionsFn]);
};

/**
 * Hook creator for Fetch All Sessions for Export action.
 */
export const useCreateFetchAllSessionsForExportAction = (
    authUserRef,
    exportUserSessionsFn // Cloud function ref
) => {
    return useCallback(async (profileId, startDateObj = null, endDateObj = null) => {
        const currentUserId = authUserRef.current?.uid;
        console.log(`[fetchAllSessionsForExport Action] Called. Profile: ${profileId}, User: ${currentUserId}`);
        if (!currentUserId || !profileId) {
            console.warn("[fetchAllSessionsForExport Action] Auth/profile ID missing.");
            return { success: false, error: "Authentication or profile ID missing.", sessions: [] };
        }
        if (!exportUserSessionsFn) {
            console.error("[fetchAllSessionsForExport Action] Cloud function reference not available.");
            return { success: false, error: "Session export service unavailable.", sessions: [] };
        }

        let startDateISO = null;
        if (startDateObj instanceof Date && !isNaN(startDateObj)) {
            startDateISO = startDateObj.toISOString();
        }
        let endDateISO = null;
        if (endDateObj instanceof Date && !isNaN(endDateObj)) {
            const endOfDayDate = new Date(endDateObj);
            endOfDayDate.setHours(23, 59, 59, 999);
            endDateISO = endOfDayDate.toISOString();
        }

        const payload = { profileId, startDate: startDateISO, endDate: endDateISO };

        try {
            console.log("[fetchAllSessionsForExport Action] Calling backend with payload:", payload);
            const result = await exportUserSessionsFn(payload);

            if (result?.data?.success) {
                 console.log(`[fetchAllSessionsForExport Action] Success. Fetched: ${result.data.sessions?.length} sessions for export.`);
                return { success: true, sessions: result.data.sessions || [] };
            } else {
                const errorMessage = result?.data?.message || "Failed to fetch sessions for export.";
                console.error(`[fetchAllSessionsForExport Action] Backend function failed: ${errorMessage}`, result?.data);
                return { success: false, error: errorMessage, sessions: [] };
            }
        } catch (err) {
            console.error("[fetchAllSessionsForExport Action] Error calling cloud function:", err);
            const message = err.message || "An unexpected error occurred fetching sessions for export.";
            return { success: false, error: message, sessions: [] };
        }
    }, [authUserRef, exportUserSessionsFn]);
};